import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNotices } from '../context/NoticeContext';
import type { NewNotice } from '../types/notice';

interface NoticeFormProps {
  onBack: () => void;
  editId?: number;
  initialTitle?: string;
  initialContent?: string;
}

const NoticeForm: React.FC<NoticeFormProps> = ({
  onBack,
  editId,
  initialTitle = '',
  initialContent = '',
}) => {
  const { addNotice, editNotice, loading, error } = useNotices();
  const [title, setTitle] = useState<string>(initialTitle);
  const [content, setContent] = useState<string>(initialContent);
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  const isEditMode = !!editId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      setFormError('제목과 내용을 모두 입력해주세요.');
      return;
    }

    if (!password) {
      setFormError('비밀번호를 입력해주세요.');
      return;
    }

    let success = false;
    try {
      if (editId) {
        success = await editNotice(editId, title, content, password);
      } else {
        if (password !== confirmPassword) {
          setFormError('비밀번호가 일치하지 않습니다.');
          return;
        }

        const newNotice: NewNotice = {
          title,
          content,
          password,
        };
        success = await addNotice(newNotice);
      }

      if (success) {
        onBack();
      }
    } catch (err) {
      setFormError('오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <BackIcon>&larr;</BackIcon>
          <span>취소</span>
        </BackButton>
        <Title>{isEditMode ? '공지사항 수정' : '공지사항 작성'}</Title>
      </Header>

      <FormContainer onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="title">제목</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="제목을 입력하세요"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="content">내용</Label>
          <TextArea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="내용을 입력하세요"
            rows={10}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">비밀번호</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isEditMode ? '기존 비밀번호를 입력하세요' : '비밀번호를 입력하세요'}
            required
          />
        </FormGroup>

        {!isEditMode && (
          <FormGroup>
            <Label htmlFor="confirmPassword">비밀번호 확인</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="비밀번호를 다시 입력하세요"
              required
            />
          </FormGroup>
        )}

        {(formError || error) && <ErrorMessage>{formError || error}</ErrorMessage>}

        <ButtonGroup>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? '처리 중...' : isEditMode ? '수정하기' : '등록하기'}
          </SubmitButton>
        </ButtonGroup>
      </FormContainer>
    </Container>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 5px;
  position: absolute;
  left: 0;
`;

const BackIcon = styled.span`
  font-size: 20px;
  margin-right: 5px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
  text-align: center;
`;

const FormContainer = styled.form`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background-color: #fff;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: #357ab7;
  }

  &:disabled {
    background-color: #a0c4e8;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-bottom: 15px;
  text-align: center;
`;

export default NoticeForm;
